<template lang="pug">
yz-filter-menu(:label="$t('panel.treatment')" :is-active="isActive" :display-value="displayValue" :disable-apply="selectedTreatments.length === 0" :loading="loading" :max-height="500" :show-header="false" @update:menu="onMenuOpen" @clear="onClear" @apply="onApply")
  .menu-header.sticky.top-0.z-1
    .font-semibold.text-sm.pa-4 {{ $t('filter_by') }} {{ $t('panel.treatment').toLowerCase() }}
    yz-text-field.text-sm.mx-4.mb-1(v-model="searchText" :placeholder="$t('search')" size="lg" @input="onQueryChanged")
    .d-flex.align-center.justify-end.px-4.py-2(v-if="count > pageSize")
      yz-pagination(v-model="page" :page-size="pageSize" :count="count" :loading="loading" arrow, type="item")

    yz-divider

  .scrollable-content
    yz-skeleton-loader.pa-4(v-if="loading" type="text@10")

    div(v-else-if="error")
      .text-center.py-8
        .text-xs.neutral500--text.mb-3 {{ $t('error_message.error_occured') }}
        yz-btn(size="sm" variant="outlined" color="primary" @click="fetchTreatments") {{ $t('retry') }}

    div(v-else)
      template(v-if="filteredTreatments.length > 0")
        div(v-for="(treatment, index) in filteredTreatments" :key="treatment.id")
          .d-flex.text-sm.px-4.py-3.cursor-pointer.hover-bg(@click="onToggleTreatment(treatment.id)")
            yz-checkbox(:value="selectedTreatments.includes(treatment.id)" @click.native.prevent)
            .ml-4 {{ treatment.name }}
          yz-divider.my-0(v-if="index < filteredTreatments.length - 1")
      template(v-else)
        .text-center.py-8.text-xs.neutral500--text {{ $t('panel.no_items_found') }}
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters } from 'vuex'

export default {
  props: {
    filter: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      treatments: [],
      selectedTreatments: [],
      searchText: '',
      loading: false,
      isActive: false,
      displayValue: '',
      treatmentsLoaded: false,
      error: false,
      page: 1,
      pageSize: 10,
      count: 0
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    filteredTreatments() {
      return this.treatments
    }
  },
  watch: {
    filter: {
      handler() {
        if (!this.filter.key) {
          this.selectedTreatments = []
          this.isActive = false
          this.displayValue = ''
        }
      }
    },
    page() {
      this.fetchTreatments()
    }
  },
  methods: {
    onMenuOpen(isOpen) {
      if (isOpen && !this.treatmentsLoaded) {
        this.fetchTreatments()
      }
    },
    onQueryChanged: debounce(function() {
      this.page = 1
      this.fetchTreatments()
    }, 750),
    async fetchTreatments() {
      this.loading = true
      this.error = false
      try {
        const params = {
          status: 'published',
          page: this.page,
          page_size: this.pageSize
        }
        if (this.searchText) {
          params.search = this.searchText
        }
        const response = await this.$axios.get(`/clinics/${this.company.slug}/treatment_products`, { params })
        this.treatments = (response.data.results || response.data || []).map(treatment => ({
          id: treatment.id,
          name: treatment.code ? `${treatment.code} - ${treatment.name}` : treatment.name,
          code: treatment.code
        }))
        this.count = response.data.count || 0
        this.treatmentsLoaded = true
      } catch (error) {
        console.error('Error fetching treatments:', error)
        this.treatments = []
        this.error = true
      } finally {
        this.loading = false
      }
    },
    onToggleTreatment(treatmentId) {
      const index = this.selectedTreatments.indexOf(treatmentId)
      if (index > -1) {
        this.selectedTreatments.splice(index, 1)
      } else {
        this.selectedTreatments.push(treatmentId)
      }
    },
    onClear() {
      this.selectedTreatments = []
      this.searchText = ''
      this.isActive = false
      this.displayValue = ''
      this.$emit('update:filter', {})
    },
    onApply() {
      this.isActive = true
      const count = this.selectedTreatments.length

      if (count <= 2) {
        const labels = this.selectedTreatments.map(id => this.treatments.find(t => t.id === id)?.name)
        this.displayValue = labels.join(', ')
      } else {
        this.displayValue = `${count} ${this.$t('selected')}`
      }

      this.$emit('update:filter', {
        key: 'products',
        value: this.selectedTreatments.join(',')
      })
    },
    reset() {
      this.onClear()
    },
    async restoreFromParams(params) {
      // Restore filter state from URL params
      if (params.treatment_products) {
        // Make sure treatments are loaded first
        if (!this.treatmentsLoaded) {
          await this.fetchTreatments()
        }
        // Parse comma-separated IDs
        this.selectedTreatments = params.treatment_products.split(',').map(id => parseInt(id))
        this.onApply()
      }
    }
  }
}
</script>

<style scoped>
.hover-bg:hover {
  background-color: #f5f5f5;
}

.menu-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  overflow: hidden;
}

.scrollable-content {
  max-height: 300px;
  overflow-y: auto;
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}
</style>
